/**
 * SEND page. Guest-first: choose files (or type text) → an ephemeral
 * room is created → the code appears (and the ENVR1- text for QR) → when the
 * receiver joins, the transfer goes directly over WebRTC with progress.
 *
 * Send to SEVERAL receivers at once ("multi-room"): the same set of
 * files/text can be sent to N people by opening an independent room (code)
 * for each one. The protocol already supports it — each WebRTC channel
 * runs its own `sendFiles` in parallel and the sources (`blobSource`) are
 * stateless, so they are shared without corrupting. Each receiver sees their own
 * progress; the sender follows them all at once.
 */

import { createRoute, Link } from "@tanstack/solid-router";
import { createSignal, For, onCleanup, Show } from "solid-js";
import { createStore, produce } from "solid-js/store";
import { Check, Copy, FileUp, FolderUp, Plus, RotateCcw, Text as TextIcon } from "@fleet/icons";
import { msg } from "@lingui/core/macro";
import {
  blobSource,
  buildTransferCode,
  buildTransferUrl,
  type FileSource,
  sendFiles,
  sendText,
  type TransferProgress,
} from "@enviar/core";
import { rootRoute } from "@/routes/__root";
import { useI18n } from "@/providers/i18n-provider";
import { Button } from "@/components/ui/button";
import { PageBackground } from "@/components/ui/page-background";
import { TopBar } from "@/components/ui/top-bar";
import { SendMark } from "@/components/brand/send-mark";
import { QrCode } from "@/components/transfer/qr-code";
import { hostTransferSession, type TransferSession } from "@/features/transfer/webrtc";
import { addHistoryEntry } from "@/features/transfer/history";
import { recordSendAchievements } from "@/features/transfer/achievements";
import { isNativePlatform, openFilesNative } from "@/lib/fs";

type Phase = "pick" | "live";
type SlotPhase = "waiting" | "sending" | "done" | "error";

/** How long the "Copied" state is shown after copying the code. */
const COPIED_RESET_MS = 1600;

/**
 * DEV-ONLY knob for the resumption e2e: `?slow=1` slows the pumping
 * (small chunks + pause per window) so the connection can be cut halfway
 * deterministically. In production (`import.meta.env.PROD`) it does not exist.
 */
function isSlowModeForE2e(): boolean {
  return import.meta.env.DEV && new URLSearchParams(globalThis.location.search).has("slow");
}

const SLOW_CHUNK_BYTES = 4096;
const SLOW_SLICE_DELAY_MS = 15;

/** Wraps a source adding per-window latency (only e2e slow mode). */
function pacedSource(source: FileSource): FileSource {
  return {
    ...source,
    async slice(start, end) {
      await new Promise((resolve) => setTimeout(resolve, SLOW_SLICE_DELAY_MS));
      return source.slice(start, end);
    },
  };
}

/**
 * Name a file travels with: if it came from "choose folder", it keeps the
 * relative path (`folder/sub/file.ext`) so the receiver reconstructs the
 * structure; the receiver ALWAYS sanitizes it segment by segment in @enviar/core.
 */
function wireName(file: File): string {
  const rel = file.webkitRelativePath;
  return rel !== undefined && rel !== "" ? rel : file.name;
}

interface Slot {
  id: number;
  phase: SlotPhase;
  code?: string;
  percent: number;
  errorKind?: string;
  /** Bytes already held by the receiver on accept (resumption); 0 = from scratch. */
  resumedFrom?: number;
}

function formatBytes(n: number): string {
  if (n < 1024) {
    return `${n} B`;
  }
  if (n < 1024 * 1024) {
    return `${(n / 1024).toFixed(1)} KB`;
  }
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function percentOf(p: TransferProgress | undefined): number {
  if (!p || p.totalBytes === 0) {
    return 0;
  }
  return Math.round((p.totalSentBytes / p.totalBytes) * 100);
}

function SendPage() {
  const { t } = useI18n();
  const [phase, setPhase] = createSignal<Phase>("pick");
  const [files, setFiles] = createSignal<File[]>([]);
  const [text, setText] = createSignal("");
  const [slots, setSlots] = createStore<Slot[]>([]);
  const [copiedId, setCopiedId] = createSignal<number>();

  // Non-reactive state: sessions per slot, sources frozen on start and
  // guards to log history/achievements only once.
  const sessions = new Map<number, TransferSession>();
  let sources: FileSource[] = [];
  let bodyText = "";
  let historyLogged = false;
  let nextId = 0;

  onCleanup(() => {
    for (const session of sessions.values()) {
      session.cancel();
    }
  });

  const totalBytes = () => files().reduce((sum, f) => sum + f.size, 0);
  const hasPayload = () => files().length > 0 || text().trim() !== "";
  const slotIndex = (id: number) => slots.findIndex((s) => s.id === id);

  const copyCode = (id: number, value: string) => {
    const run = async () => {
      try {
        await navigator.clipboard.writeText(value);
        setCopiedId(id);
        setTimeout(() => {
          setCopiedId((cur) => (cur === id ? undefined : cur));
        }, COPIED_RESET_MS);
      } catch {
        // Clipboard not available (insecure context / permission denied) — ignore.
      }
    };
    void run();
  };

  const logHistoryOnce = () => {
    if (historyLogged || files().length === 0) {
      return;
    }
    historyLogged = true;
    addHistoryEntry({
      direction: "sent",
      fileNames: files().map((f) => f.name),
      totalBytes: totalBytes(),
    });
    recordSendAchievements({ files: files().length, bytes: totalBytes() });
  };

  const openSlot = () => {
    const id = nextId;
    nextId += 1;
    setSlots(produce((list) => list.push({ id, phase: "waiting", percent: 0 })));

    const session = hostTransferSession({
      onCode: (c) => {
        const i = slotIndex(id);
        if (i !== -1) {
          setSlots(i, "code", c);
        }
      },
      onChannel: (channel) => {
        const i = slotIndex(id);
        if (i !== -1) {
          setSlots(i, "phase", "sending");
        }
        if (bodyText !== "") {
          sendText(channel, bodyText);
        }
        sendFiles(channel, sources, {
          ...(isSlowModeForE2e() ? { chunkSize: SLOW_CHUNK_BYTES } : {}),
          onAccept: (resume) => {
            const already = resume.reduce((sum, n) => sum + n, 0);
            if (already > 0) {
              const j = slotIndex(id);
              if (j !== -1) {
                setSlots(j, "resumedFrom", already);
              }
            }
          },
          onProgress: (p) => {
            const j = slotIndex(id);
            if (j !== -1) {
              setSlots(j, "percent", percentOf(p));
            }
          },
          onDone: () => {
            const j = slotIndex(id);
            if (j !== -1) {
              setSlots(j, "phase", "done");
            }
            logHistoryOnce();
            channel.close();
          },
          onError: () => {
            const j = slotIndex(id);
            if (j !== -1) {
              setSlots(
                j,
                produce((s) => {
                  s.phase = "error";
                  s.errorKind = "send-failed";
                }),
              );
            }
          },
        });
      },
      onClosed: () => {
        const i = slotIndex(id);
        if (i !== -1 && slots[i]?.phase !== "done") {
          setSlots(
            i,
            produce((s) => {
              s.phase = "error";
              s.errorKind = "peer-gone";
            }),
          );
        }
      },
      onFailure: (kind) => {
        const i = slotIndex(id);
        if (i !== -1) {
          setSlots(
            i,
            produce((s) => {
              s.phase = "error";
              s.errorKind = kind;
            }),
          );
        }
      },
    });
    sessions.set(id, session);
  };

  // Native: opens the OS dialog and replaces the file selection.
  const pickFilesNative = async () => {
    const picked = await openFilesNative();
    if (picked.length > 0) {
      setFiles(picked);
    }
  };

  const start = () => {
    if (!hasPayload()) {
      return;
    }
    // Freezes the sources once: `blobSource` is stateless and is shared without
    // problems among the concurrent `sendFiles` of each receiver. `wireName`
    // keeps the relative path when the origin was "choose folder".
    sources = files().map((f) => blobSource(wireName(f), f, f.type));
    if (isSlowModeForE2e()) {
      sources = sources.map(pacedSource);
    }
    bodyText = text().trim();
    setPhase("live");
    openSlot();
  };

  return (
    <main
      class="app-screen screen-enter mx-auto w-full max-w-xl gap-7"
      // In "live" mode the screen widens on desktop so the receiver slots
      // (multi-room) form a grid instead of a long column.
      classList={{ "lg:max-w-5xl": phase() === "live" }}
    >
      <PageBackground />
      <TopBar />

      <h1 class="font-display text-3xl tracking-tight">{t(msg`Send`)}</h1>

      <Show when={phase() === "pick"}>
        <div class="mx-auto flex w-full max-w-xl flex-col gap-4">
          <label
            class="group flex cursor-pointer flex-col items-center gap-3 rounded-[var(--radius-2xl)] border-2 border-dashed border-border bg-surface/50 p-8 text-center backdrop-blur transition-colors hover:border-accent/50 hover:bg-accent/5 sm:p-10"
            // Native (Tauri): opens the OS "Open…" dialog instead of the WebView
            // selector, and reads the files from disk directly. Web: lets
            // the standard `<input>` invoke the browser picker.
            onClick={(e) => {
              if (isNativePlatform()) {
                e.preventDefault();
                void pickFilesNative();
              }
            }}
          >
            <span class="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-accent/12 text-accent transition-transform group-hover:scale-105">
              <FileUp class="h-7 w-7" />
            </span>
            <span class="text-base font-semibold text-foreground">{t(msg`Choose files`)}</span>
            <span class="text-sm text-muted-foreground">{t(msg`Tap to pick one or many`)}</span>
            <input
              type="file"
              multiple
              data-testid="input-files"
              onChange={(e) => setFiles(e.currentTarget.files ? [...e.currentTarget.files] : [])}
              class="sr-only"
            />
          </label>

          {/* Carpeta ENTERA (web): el navegador lista recursivamente los ficheros
              con su ruta relativa (`webkitRelativePath`), que viaja en el
              manifiesto para que el receptor conserve la estructura. */}
          <Show when={!isNativePlatform()}>
            <label class="group flex cursor-pointer items-center justify-center gap-2 rounded-[var(--radius-xl)] border border-dashed border-border bg-surface/50 px-4 py-3 text-sm font-medium text-muted-foreground backdrop-blur transition-colors hover:border-accent/50 hover:text-foreground">
              <FolderUp class="h-4 w-4" />
              {t(msg`Or choose a whole folder`)}
              <input
                type="file"
                multiple
                // Non-standard but universal attribute (Chrome/Firefox/Safari)
                // for selecting directories; it is not in Solid's JSX
                // types, so it is set via ref.
                ref={(el) => el.setAttribute("webkitdirectory", "")}
                data-testid="input-folder"
                onChange={(e) => setFiles(e.currentTarget.files ? [...e.currentTarget.files] : [])}
                class="sr-only"
              />
            </label>
          </Show>

          <Show when={files().length > 0}>
            <ul class="flex flex-col gap-2" data-testid="picked-files">
              <For each={files()}>
                {(f) => (
                  <li class="flex items-center justify-between gap-3 rounded-xl border border-border bg-surface/70 px-4 py-2.5 text-sm backdrop-blur">
                    <span class="truncate font-medium text-foreground">{wireName(f)}</span>
                    <span class="shrink-0 text-xs text-muted-foreground">
                      {formatBytes(f.size)}
                    </span>
                  </li>
                )}
              </For>
            </ul>
          </Show>

          <label class="flex flex-col gap-2 rounded-[var(--radius-xl)] border border-border bg-surface/70 p-4 backdrop-blur">
            <span class="inline-flex items-center gap-2 text-sm font-semibold text-foreground">
              <TextIcon class="h-4 w-4 text-muted-foreground" />
              {t(msg`Or send text / clipboard`)}
            </span>
            <textarea
              data-testid="input-text"
              rows="2"
              value={text()}
              onInput={(e) => setText(e.currentTarget.value)}
              placeholder={t(msg`Paste a note, link or snippet…`)}
              class="w-full resize-none rounded-lg border border-field-border bg-field-background p-3 text-sm text-field-foreground outline-none transition-shadow placeholder:text-field-placeholder focus:ring-2 focus:ring-accent/40"
            />
          </label>

          <Button
            data-testid="btn-start"
            onClick={start}
            disabled={!hasPayload()}
            size="lg"
            class="gap-2 text-base"
          >
            <SendMark class="h-5 w-5" />
            {t(msg`Create link`)}
          </Button>
        </div>
      </Show>

      <Show when={phase() === "live"}>
        <p class="text-sm text-muted-foreground">
          {t(msg`Share a code with each device. Send to several people at once.`)}
        </p>

        {/*
         * Adaptive multi-receiver: on mobile the slots stack; on tablet and
         * desktop they form a grid (2–3 columns) so that "send to several"
         * fits at a glance without an endless column.
         */}
        <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
          <For each={slots}>
            {(slot) => (
              <div
                class="relative overflow-hidden rounded-[var(--radius-2xl)] border border-border bg-surface/70 p-6 text-center shadow-md backdrop-blur"
                data-testid={`receiver-slot-${slot.id}`}
              >
                <Show when={slot.phase !== "error"}>
                  <p class="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    {t(msg`Enter this code on the other device`)}
                  </p>

                  {/* Código grande con ondas de señal irradiando (en vivo) */}
                  <div class="relative mx-auto my-4 flex items-center justify-center">
                    <Show when={slot.phase === "waiting"}>
                      <span
                        aria-hidden="true"
                        class="animate-signal absolute h-24 w-24 rounded-full border border-accent/40"
                      />
                      <span
                        aria-hidden="true"
                        class="animate-signal absolute h-24 w-24 rounded-full border border-accent/40"
                        style={{ "animation-delay": "0.9s" }}
                      />
                    </Show>
                    <div
                      class="relative font-display text-[3.25rem] leading-none tracking-[0.18em] text-foreground tabular-nums"
                      data-testid="code-display"
                    >
                      <Show
                        when={slot.code}
                        fallback={<span class="text-muted-foreground">·····</span>}
                      >
                        {slot.code}
                      </Show>
                    </div>
                  </div>

                  <Show when={slot.code}>
                    <button
                      type="button"
                      onClick={() => {
                        copyCode(slot.id, buildTransferCode(slot.code ?? ""));
                      }}
                      class="press mx-auto inline-flex items-center gap-2 rounded-full border border-border bg-background/60 px-3 py-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground"
                    >
                      <Show
                        when={copiedId() === slot.id}
                        fallback={
                          <>
                            <Copy class="h-3.5 w-3.5" />
                            <code data-testid="transfer-code">
                              {buildTransferCode(slot.code ?? "")}
                            </code>
                          </>
                        }
                      >
                        <Check class="h-3.5 w-3.5 text-success" />
                        {t(msg`Copied`)}
                        <code class="sr-only" data-testid="transfer-code">
                          {buildTransferCode(slot.code ?? "")}
                        </code>
                      </Show>
                    </button>
                  </Show>
                </Show>

                <Show when={slot.phase === "waiting"}>
                  {/* QR de la URL profunda: cualquier cámara (sistema o in-app)
                      abre /receive con el código puesto y conecta sola. */}
                  <Show when={slot.code}>
                    {(code) => (
                      <div class="mt-4 flex justify-center">
                        <QrCode
                          payload={buildTransferUrl(globalThis.location.origin, code())}
                          class="h-32 w-32"
                        />
                      </div>
                    )}
                  </Show>
                  <p class="mt-4 inline-flex items-center gap-2 text-sm text-muted-foreground">
                    <span class="relative flex h-2 w-2">
                      <span class="animate-wave-ping absolute inline-flex h-full w-full rounded-full bg-accent/70" />
                      <span class="relative inline-flex h-2 w-2 rounded-full bg-accent" />
                    </span>
                    {t(msg`Waiting for the other device…`)}
                  </p>
                </Show>

                <Show when={slot.phase === "sending"}>
                  <div class="mt-4 w-full" data-testid="transfer-progress">
                    <div class="h-2.5 w-full overflow-hidden rounded-full bg-muted/25">
                      <div
                        class="h-full rounded-full bg-gradient-to-r from-accent to-brand-purple transition-[width] duration-300 ease-out"
                        style={{ width: `${slot.percent}%` }}
                      />
                    </div>
                    <p class="mt-2 font-display text-lg text-foreground">{slot.percent}%</p>
                    <p class="text-xs text-muted-foreground">{t(msg`Sending…`)}</p>
                  </div>
                </Show>

                {/* Nota de reanudación: visible durante el envío Y al terminar
                    (un reenvío parcial puede completarse en milisegundos). */}
                <Show
                  when={(slot.phase === "sending" || slot.phase === "done") && slot.resumedFrom}
                >
                  {(bytes) => (
                    <p
                      class="mt-1 text-xs text-muted-foreground"
                      data-testid="resumed-from"
                      data-bytes={bytes()}
                    >
                      {t(msg`Resumed — the receiver already had part of it.`)}
                    </p>
                  )}
                </Show>

                <Show when={slot.phase === "done"}>
                  <div class="flex flex-col items-center gap-2 py-2">
                    <span class="inline-flex h-14 w-14 items-center justify-center rounded-full bg-success/15 text-success">
                      <SendMark class="animate-send-off h-7 w-7" />
                    </span>
                    <p class="font-display text-xl text-foreground" data-testid="transfer-done">
                      {t(msg`Sent!`)}
                    </p>
                  </div>
                </Show>

                <Show when={slot.phase === "error"}>
                  <div
                    class="flex flex-col items-center gap-2 py-2"
                    data-testid="transfer-error"
                    role="alert"
                    aria-live="assertive"
                  >
                    <p class="text-sm font-medium text-destructive">
                      {t(msg`Transfer could not complete.`)}
                    </p>
                    <p class="text-xs text-muted-foreground">{slot.errorKind}</p>
                    {/* Reintento: abre un slot nuevo (código nuevo) con la MISMA
                        carga. Si el receptor conservó lo parcial, el protocolo
                        reanuda desde donde iba (accept.resume). */}
                    <Button
                      variant="outline"
                      size="sm"
                      data-testid="btn-retry-slot"
                      onClick={openSlot}
                      class="gap-2"
                    >
                      <RotateCcw class="h-3.5 w-3.5" />
                      {t(msg`Retry with a new code`)}
                    </Button>
                  </div>
                </Show>
              </div>
            )}
          </For>
        </div>

        <div class="mx-auto flex w-full max-w-xl flex-col gap-4">
          <Button variant="outline" onClick={openSlot} class="gap-2" data-testid="btn-add-receiver">
            <Plus class="h-4 w-4" />
            {t(msg`Add another receiver`)}
          </Button>

          <Link
            to="/send"
            class="text-center text-sm font-medium text-accent underline-offset-4 hover:underline"
          >
            {t(msg`Send something else`)}
          </Link>
        </div>
      </Show>
    </main>
  );
}

export const sendRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/send",
  component: SendPage,
});
